import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '../components/dashboard/Sidebar';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import PerformanceTrackingService from '../services/PerformanceTrackingService';
import { AuthContext } from '../context/AuthContext';
import api from '../api/api';
import GradientButton from '../components/ui/GradientButton';

/**
 * Candidate Profile Page
 * 
 * This page displays detailed information about a candidate,
 * including their skills, experience, education, and AI-generated insights.
 * 
 * This page is specifically for viewing candidate profiles (employer view),
 * not for editing profiles. Editing functionality is in ProfileSettingsPage.
 * 
 * Access control: This page is accessible to employers only when viewing candidates,
 * and to the candidate themselves when viewing their own profile.
 */
const CandidateProfilePage = () => {
  const { candidateId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [isFromAiSearch, setIsFromAiSearch] = useState(false);
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  const [canViewContact, setCanViewContact] = useState(false);

  // Get authenticated user from context
  const { user, isAuthenticated } = useContext(AuthContext);
  
  // Check if user is on paid plan (can view contact info)
  useEffect(() => {
    if (!isAuthenticated || !user) {
      setCanViewContact(false);
      return;
    }
    
    // Candidates can always see their own contact
    if (user.userType === 'candidate') {
      setCanViewContact(true);
      return;
    }
    
    // Employers/recruiters need paid plan (not starter)
    if (user.userType === 'employer' || user.userType === 'recruiter') {
      const subscriptionPlan = user.subscriptionPlan || 'starter';
      // Can view if on any paid plan (pro, enterprise, or any custom plan that's not starter)
      setCanViewContact(subscriptionPlan !== 'starter' && subscriptionPlan !== '');
    }
  }, [user, isAuthenticated]);
  
  // Check if coming from AI search and if viewing own profile
  useEffect(() => {
    // Check if the user came from an AI search page with real match data
    const fromAiSearch = location.state?.fromAiSearch || false;
    const hasMatchData = location.state?.matchScore !== undefined;
    setIsFromAiSearch(fromAiSearch && hasMatchData);
    
    // Check if user is viewing their own profile using AuthContext
    if (isAuthenticated && user) {
      if (user.id === candidateId || (!candidateId && user.userType === 'candidate')) {
        setIsOwnProfile(true);
      } else {
        setIsOwnProfile(false);
      }
    }
  }, [location, candidateId, user, isAuthenticated]);

  // Fetch candidate data from API
  useEffect(() => {
    const fetchCandidate = async () => {
      try {
        setLoading(true);
        
        // Fetch real candidate data from backend
        const response = await api.get(`/candidates/${candidateId}`);
        const data = response.data;
        
        console.log('API Response:', data); // Debug log
        
        // Check if we got valid candidate data
        // Handle multiple response formats
        let candidateData = null;
        
        if (data.success && data.data?.candidate) {
          candidateData = data.data.candidate;
        } else if (data.success && data.candidate) {
          candidateData = data.candidate;
        } else if (data.candidate) {
          candidateData = data.candidate;
        }
        
        if (!candidateData) {
          throw new Error('Candidate data not found');
        }
        
        // Ensure skills is an array
        if (candidateData.skills && !Array.isArray(candidateData.skills)) {
          if (typeof candidateData.skills === 'string') {
            candidateData.skills = candidateData.skills.split(',').map(s => s.trim()).filter(s => s);
          } else {
            candidateData.skills = [];
          }
        }
        
        candidateData = candidateData || {
          id: candidateId || 'unknown',
          name: 'Unknown Candidate',
          title: 'Position Not Specified',
          experience: 6,
          location: 'San Francisco, CA',
          remote: true,
          avatar: `https://ui-avatars.com/api/?name=Alex+Johnson&background=random`,
          skills: ['JavaScript', 'React', 'Node.js', 'MongoDB', 'Express', 'TypeScript', 'AWS'],
          education: [
            { degree: 'Bachelor of Computer Science', institution: 'Stanford University', year: '2018' }
          ],
          workHistory: [
            {
              company: 'Tech Innovations Inc.',
              role: 'Senior Developer',
              duration: '2020 - Present',
              description: 'Led development of multiple web applications using React and Node.js.'
            },
            {
              company: 'StartupXYZ',
              role: 'Full Stack Developer',
              duration: '2018 - 2020',
              description: 'Developed and maintained e-commerce platform using MERN stack.'
            }
          ],
          projects: [
            {
              name: 'E-commerce Platform',
              description: 'Built a scalable e-commerce platform with React, Node.js, and MongoDB.',
              technologies: ['React', 'Node.js', 'MongoDB', 'Express', 'Redux']
            },
            {
              name: 'AI-Powered Task Manager',
              description: 'Developed a task management application with AI-based prioritization.',
              technologies: ['TypeScript', 'React', 'Python', 'TensorFlow']
            }
          ],
          contact: {
            email: 'alex.johnson@example.com',
            phone: '+1 (555) 123-4567',
            linkedin: 'linkedin.com/in/alexjohnson'
          }
        };
        
        // Normalize candidate data - preserve Excel data
        if (!Array.isArray(candidateData.education)) {
          // Keep string education data from Excel
          if (candidateData.education && typeof candidateData.education === 'string') {
            // Education is already a string, keep it as is
          } else {
            candidateData.education = [];
          }
        }
        if (!Array.isArray(candidateData.skills)) {
          if (typeof candidateData.skills === 'string' && candidateData.skills) {
            candidateData.skills = candidateData.skills.split(',').map(s => s.trim()).filter(s => s);
          } else {
            candidateData.skills = [];
          }
        }
        if (!Array.isArray(candidateData.workHistory)) {
          candidateData.workHistory = [];
        }
        if (!Array.isArray(candidateData.projects)) {
          candidateData.projects = [];
        }
        
        // Only set AI insights if coming from AI search with real match data
        if (location.state?.fromAiSearch && location.state?.matchScore) {
          const aiInsightsData = {
            match_score: location.state.matchScore,
            match_reason: location.state.matchReason || 'AI-matched candidate',
            match_details: location.state.matchDetails || []
          };
          setAiInsights(aiInsightsData);
        } else {
          setAiInsights(null);
        }
        
        setCandidate(candidateData);
        
        // Track candidate profile view for performance metrics (optional - silent fail)
        if (!isOwnProfile && user?.userType !== 'candidate') {
          PerformanceTrackingService.trackCandidateReview({
            id: candidateData.id,
            name: candidateData.name
          }).catch(err => {
            // Silent fail - analytics shouldn't break user experience
            console.warn('Analytics tracking failed:', err);
          });
        }
        
        setLoading(false);
      } catch (err) {
        setError('Failed to load candidate profile');
        setLoading(false);
        console.error('Error fetching candidate:', err);
      }
    };
    
    fetchCandidate();
  }, [candidateId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
        <div className="animate-pulse text-center">
          <p className="text-lg text-gray-600">Loading candidate profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg text-red-600">{error}</p>
          <div className="mt-4">
            <GradientButton onClick={() => navigate(-1)}>Go Back</GradientButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{candidate.name}</h1>
              {candidate.title && <p className="text-xl text-gray-600">{candidate.title}</p>}
              <div className="mt-2 flex items-center text-gray-500">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                <span>{candidate.location}{candidate.state && candidate.state !== candidate.location ? `, ${candidate.state}` : ''}</span>
              </div>
            </div>
            <div className="mt-4 md:mt-0 flex items-center space-x-3">
              {isOwnProfile ? (
                <GradientButton onClick={() => navigate('/candidate/profile')}>
                  Edit Profile
                </GradientButton>
              ) : (
                <GradientButton onClick={() => {
                  // Determine correct inbox path based on user type
                  const inboxPath = user?.userType === 'recruiter' 
                    ? `/recruiter/inbox?candidateId=${candidateId}&candidateName=${encodeURIComponent(candidate.name)}`
                    : `/inbox?candidateId=${candidateId}&candidateName=${encodeURIComponent(candidate.name)}`;

                  navigate(inboxPath);
                }}>
                  Contact
                </GradientButton>
              )}
              <GradientButton 
                onClick={() => isOwnProfile ? navigate('/jobs') : navigate('/candidates')}
                variant="secondary"
              >
                {isOwnProfile ? 'Back to Jobs' : 'Back to Candidates'}
              </GradientButton>
            </div>
          </div>
        </div>

        <div className={`grid grid-cols-1 gap-6 ${aiInsights ? 'lg:grid-cols-3' : ''}`}>
          {/* Main Content */}
          <div className={aiInsights ? 'lg:col-span-2' : ''}>
            {/* Skills */}
            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Skills</h2>
              <div className="flex flex-wrap gap-2">
                {Array.isArray(candidate.skills) && candidate.skills.map((skill, index) => (
                  <span 
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>

            {/* Work History/Experience */}
            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Experience</h2>
              <div className="space-y-6">
                {Array.isArray(candidate.workHistory) && candidate.workHistory.length > 0 ? (
                  candidate.workHistory.map((work, index) => (
                    <div key={index} className="border-l-2 border-indigo-500 pl-4">
                      <h3 className="text-lg font-medium text-gray-900">{work.role}</h3>
                      <div className="text-gray-600 font-medium">{work.company}</div>
                      <div className="flex items-center mt-1 text-sm text-gray-500">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                        </svg>
                        <span>{work.duration}</span>
                      </div>
                      <p className="mt-2 text-gray-600">{work.description}</p>
                    </div>
                  ))
                ) : (
                  <div>
                    {candidate.experience && (
                      <div className="flex items-center text-gray-700">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                        </svg>
                        <span className="font-medium">Total Experience:</span>
                        <span className="ml-2">{candidate.experience}</span>
                      </div>
                    )}
                    {candidate.currentCompany && (
                      <div className="flex items-center text-gray-700 mt-3">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
                        </svg>
                        <span className="font-medium">Current Company:</span>
                        <span className="ml-2">{candidate.currentCompany}</span>
                      </div>
                    )}
                    {!candidate.experience && !candidate.currentCompany && (
                      <p className="text-sm text-gray-500">No work experience information available</p>
                    )}
                  </div>
                )}
              </div>
            </div>

          </div>

          {/* Sidebar - Only show if we have AI insights */}
          {aiInsights && (
            <div className="space-y-6">
              {/* AI Insights */}
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 shadow rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  <h2 className="text-xl font-semibold text-gray-900">AI Insights</h2>
                </div>
              
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Match Score</span>
                    <span className="text-sm font-semibold text-gray-900">{aiInsights.match_score}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-gradient-to-r from-indigo-500 to-purple-600 h-2 rounded-full" style={{width: `${aiInsights.match_score}%`}}></div>
                  </div>
                </div>
              
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Why This Match?</h3>
                  <p className="text-sm text-gray-600">{aiInsights.match_reason}</p>
                </div>
              
                {aiInsights.match_details && aiInsights.match_details.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Match Breakdown</h3>
                    <div className="space-y-2">
                      {aiInsights.match_details.map((detail, index) => (
                        <div key={index}>
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="text-gray-600">{detail.type}</span>
                            <span className="font-medium text-gray-900">{detail.match}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div 
                              className="bg-gradient-to-r from-blue-400 to-blue-600 h-1.5 rounded-full" 
                              style={{width: `${detail.match}%`}}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Education */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Education</h2>
            <div className="space-y-4">
              {Array.isArray(candidate.education) ? candidate.education.map((edu, index) => (
                <div key={index}>
                  <h3 className="text-base font-medium text-gray-900">{edu.degree || edu}</h3>
                  {edu.institution && <div className="text-sm text-gray-600">{edu.institution}</div>}
                  {edu.year && <div className="text-sm text-gray-500">{edu.year}</div>}
                </div>
              )) : candidate.education ? (
                <div>
                  <p className="text-base font-medium text-gray-900">{candidate.education}</p>
                </div>
              ) : (
                <p className="text-sm text-gray-500">No education information available</p>
              )}
            </div>
          </div>

          {/* Additional Details */}
          {(candidate.processName || candidate.category || candidate.bio) && (
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Additional Details</h2>
              <div className="space-y-3">
                {candidate.processName && (
                  <div>
                    <span className="text-sm font-medium text-gray-700">Process:</span>
                    <span className="ml-2 text-sm text-gray-900">{candidate.processName}</span>
                  </div>
                )}
                {candidate.category && (
                  <div>
                    <span className="text-sm font-medium text-gray-700">Category:</span>
                    <span className="ml-2 text-sm text-gray-900">{candidate.category}</span>
                  </div>
                )}
                {candidate.bio && (
                  <div>
                    <span className="text-sm font-medium text-gray-700">About:</span>
                    <p className="mt-1 text-sm text-gray-600">{candidate.bio}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Contact Information */}
          <div className="bg-white shadow rounded-lg p-6 relative">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Contact Information</h2>
            
            {/* Blur overlay for free plan users */}
            {!canViewContact && !isOwnProfile && (
              <div className="absolute inset-0 bg-white/80 backdrop-blur-sm rounded-lg flex items-center justify-center z-10">
                <div className="text-center p-6">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Upgrade to View Contact Info</h3>
                  <p className="text-sm text-gray-600 mb-4">Subscribe to Pro plan to access candidate contact details</p>
                  <GradientButton onClick={() => navigate('/pricing')}>
                    View Plans
                  </GradientButton>
                </div>
              </div>
            )}
            
            <div className={`space-y-3 ${!canViewContact && !isOwnProfile ? 'filter blur-md pointer-events-none' : ''}`}>
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
                <a href={`mailto:${candidate.contact?.email || candidate.email}`} className="text-blue-600 hover:underline">
                  {candidate.contact?.email || candidate.email}
                </a>
              </div>
              {(candidate.phone || candidate.contact?.phone) && (
                <div className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                  </svg>
                  <a href={`tel:${candidate.phone || candidate.contact?.phone}`} className="text-blue-600 hover:underline">
                    {candidate.phone || candidate.contact?.phone}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateProfilePage;
